import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LedgerEntryLine } from '../clients/rust-api-client';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../Components/Catalyst/dialog';
import { Badge } from '../Components/Catalyst/badge';
import { Button } from '../Components/Catalyst/button';
import { Text } from '../Components/Catalyst/text';
import {
  formatBillingDate,
  formatEpochDate,
  formatMoney,
  signedAmountParts,
} from '../utils/billingHelpers';
import { entryColor, entryHelp, entryLabel } from './entryLabels';

interface Props {
  entry: LedgerEntryLine | null;
  onClose: () => void;
}

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="flex items-baseline justify-between gap-4 border-b border-zinc-100 py-2.5 last:border-0 dark:border-zinc-700/60">
    <span className="text-sm text-zinc-500 dark:text-zinc-400">{label}</span>
    <span className="text-right text-sm text-zinc-900 dark:text-white">
      {value}
    </span>
  </div>
);

/**
 * One entry, with the room to explain it that a feed row does not have.
 *
 * This is where `entryHelp` earns its place: "Returned payment fee" is
 * the right label on a row, and the sentence saying it was charged
 * because a payment came back unpaid is what stops the phone call.
 */
const EntryDetailDialog: React.FC<Props> = ({ entry, onClose }) => {
  const navigate = useNavigate();

  if (!entry) return null;

  const parts = signedAmountParts(entry.amount);
  const help = entryHelp(entry.entry_type);
  const violationId =
    entry.source_type === 'violation' && entry.source_id
      ? entry.source_id.split(':')[0]
      : null;

  return (
    <Dialog open={!!entry} onClose={onClose}>
      <DialogTitle>{entryLabel(entry.entry_type)}</DialogTitle>

      <DialogBody>
        <div className="mb-4 flex items-center gap-3">
          <Badge color={entryColor(entry.entry_type)}>
            {entryLabel(entry.entry_type)}
          </Badge>
          <span
            className={`text-2xl font-semibold tabular-nums ${
              parts.isCredit
                ? 'text-lime-700 dark:text-lime-400'
                : 'text-zinc-900 dark:text-white'
            }`}
          >
            {parts.text}
          </span>
        </div>

        {help && <Text className="mb-4 text-sm">{help}</Text>}

        <Row label="Date" value={formatBillingDate(entry.effective_date)} />
        {entry.due_date && (
          <Row label="Due" value={formatBillingDate(entry.due_date)} />
        )}
        {entry.description && (
          <Row label="Description" value={entry.description} />
        )}
        <Row
          label="Balance after this"
          value={formatMoney(entry.running_balance)}
        />
        <Row label="Recorded" value={formatEpochDate(entry.posted_at)} />

        {entry.reverses_entry_id && (
          <Text className="mt-4 text-sm">
            This entry corrects an earlier one. The two cancel out, so together
            they leave your balance unchanged.
          </Text>
        )}
      </DialogBody>

      <DialogActions>
        {violationId && (
          <Button plain onClick={() => navigate(`/violation/${violationId}`)}>
            View the violation
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default EntryDetailDialog;
